<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Typography from '@tiptap/extension-typography'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Placeholder from '@tiptap/extension-placeholder'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { common, createLowlight } from 'lowlight'
import { onBeforeUnmount, onMounted, computed, ref, watch } from 'vue'
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  exportAsMarkdown,
  exportAsHtml,
  importMarkdownFile,
  clearLocalStorage,
  debounce,
  htmlToMarkdown,
  markdownToHtml,
  type ConversionResult,
} from '@/utils/fileManager'

const lowlight = createLowlight(common)

// File state
const currentFilename = ref<string>('untitled.md')
const hasUnsavedChanges = ref(false)
const lastSaved = ref<Date | null>(null)
const showFileMenu = ref(false)

// Raw mode state
const isRawMode = ref(false)
const rawMarkdown = ref('')
const rawTextarea = ref<HTMLTextAreaElement | null>(null)

// Keyboard shortcuts modal state
const showShortcutsModal = ref(false)

// Toast notification state
interface ToastMessage {
  id: number
  message: string
  type: 'error' | 'warning' | 'success'
}
const toasts = ref<ToastMessage[]>([])
let toastIdCounter = 0

function showToast(message: string, type: 'error' | 'warning' | 'success' = 'error') {
  const id = ++toastIdCounter
  toasts.value.push({ id, message, type })
  setTimeout(() => {
    toasts.value = toasts.value.filter(t => t.id !== id)
  }, 5000)
}

function dismissToast(id: number) {
  toasts.value = toasts.value.filter(t => t.id !== id)
}

function handleConversionResult(result: ConversionResult, context: string) {
  if (!result.success && result.error) {
    showToast(`${context}: ${result.error}`, 'warning')
  }
}

// Get initial content from localStorage or use default
const savedData = loadFromLocalStorage()
const initialContent = savedData.html || `
    <h1>Welcome to Supra</h1>
    <p>Start writing your markdown here...</p>
  `
if (savedData.filename) {
  currentFilename.value = savedData.filename
}

const editor = useEditor({
  content: initialContent,
  extensions: [
    StarterKit.configure({
      codeBlock: false,
      heading: {
        levels: [1, 2, 3, 4, 5, 6],
      },
    }),
    Highlight.configure({
      multicolor: true,
    }),
    Typography,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'editor-link',
      },
    }),
    Image.configure({
      inline: true,
      allowBase64: true,
    }),
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableCell,
    TableHeader,
    Placeholder.configure({
      placeholder: 'Start writing...',
    }),
    CodeBlockLowlight.configure({
      lowlight,
    }),
  ],
  editorProps: {
    attributes: {
      class: 'prose prose-invert focus:outline-none',
    },
  },
  onUpdate: () => {
    hasUnsavedChanges.value = true
  },
})

const isActive = computed(() => ({
  bold: editor.value?.isActive('bold'),
  italic: editor.value?.isActive('italic'),
  strike: editor.value?.isActive('strike'),
  code: editor.value?.isActive('code'),
  heading1: editor.value?.isActive('heading', { level: 1 }),
  heading2: editor.value?.isActive('heading', { level: 2 }),
  heading3: editor.value?.isActive('heading', { level: 3 }),
  bulletList: editor.value?.isActive('bulletList'),
  orderedList: editor.value?.isActive('orderedList'),
  taskList: editor.value?.isActive('taskList'),
  blockquote: editor.value?.isActive('blockquote'),
  codeBlock: editor.value?.isActive('codeBlock'),
  link: editor.value?.isActive('link'),
}))

function toggleBold() {
  editor.value?.chain().focus().toggleBold().run()
}

function toggleItalic() {
  editor.value?.chain().focus().toggleItalic().run()
}

function toggleStrike() {
  editor.value?.chain().focus().toggleStrike().run()
}

function toggleCode() {
  editor.value?.chain().focus().toggleCode().run()
}

function toggleHeading(level: 1 | 2 | 3 | 4 | 5 | 6) {
  editor.value?.chain().focus().toggleHeading({ level }).run()
}

function toggleBulletList() {
  editor.value?.chain().focus().toggleBulletList().run()
}

function toggleOrderedList() {
  editor.value?.chain().focus().toggleOrderedList().run()
}

function toggleTaskList() {
  editor.value?.chain().focus().toggleTaskList().run()
}

function toggleBlockquote() {
  editor.value?.chain().focus().toggleBlockquote().run()
}

function toggleCodeBlock() {
  editor.value?.chain().focus().toggleCodeBlock().run()
}

function setLink() {
  const url = window.prompt('Enter URL:')
  if (url) {
    editor.value?.chain().focus().setLink({ href: url }).run()
  }
}

function addImage() {
  const url = window.prompt('Enter image URL:')
  if (url) {
    editor.value?.chain().focus().setImage({ src: url }).run()
  }
}

function insertTable() {
  editor.value?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
}

function addHorizontalRule() {
  editor.value?.chain().focus().setHorizontalRule().run()
}

// File management functions
function saveToStorage() {
  let html: string
  if (isRawMode.value) {
    // In raw mode, convert markdown to HTML before saving
    const result = markdownToHtml(rawMarkdown.value)
    handleConversionResult(result, 'Save')
    html = result.content
    // Also update the editor content so it's in sync when switching back
    editor.value?.commands.setContent(html)
  } else {
    html = editor.value?.getHTML() || ''
  }
  if (html) {
    saveToLocalStorage(html, currentFilename.value)
    hasUnsavedChanges.value = false
    lastSaved.value = new Date()
  }
}

// Debounced autosave (saves 2 seconds after last edit)
const debouncedAutosave = debounce(() => {
  saveToStorage()
}, 2000)

// Watch for changes and trigger autosave
watch(hasUnsavedChanges, (hasChanges) => {
  if (hasChanges) {
    debouncedAutosave()
  }
})

function exportMarkdown() {
  const html = editor.value?.getHTML()
  if (html) {
    const result = exportAsMarkdown(html, currentFilename.value)
    handleConversionResult(result, 'Export')
  }
  showFileMenu.value = false
}

function exportHtml() {
  const html = editor.value?.getHTML()
  if (html) {
    const htmlFilename = currentFilename.value.replace(/\.md$/, '.html')
    exportAsHtml(html, htmlFilename)
  }
  showFileMenu.value = false
}

async function openFile() {
  try {
    const { content, filename, conversionResult } = await importMarkdownFile()
    editor.value?.commands.setContent(content)
    currentFilename.value = filename
    hasUnsavedChanges.value = false
    handleConversionResult(conversionResult, 'Import')
    saveToStorage() // Save immediately after loading
  } catch (error) {
    // User cancelled or error - silently ignore
  }
  showFileMenu.value = false
}

function newFile() {
  const confirmNew = !hasUnsavedChanges.value || window.confirm('You have unsaved changes. Create new file anyway?')
  if (confirmNew) {
    editor.value?.commands.setContent('<h1>New Document</h1><p>Start writing...</p>')
    currentFilename.value = 'untitled.md'
    hasUnsavedChanges.value = false
    clearLocalStorage()
    lastSaved.value = null
  }
  showFileMenu.value = false
}

function renameFile() {
  const newName = window.prompt('Enter filename:', currentFilename.value)
  if (newName) {
    currentFilename.value = newName.endsWith('.md') ? newName : `${newName}.md`
    saveToStorage()
  }
  showFileMenu.value = false
}

function toggleFileMenu() {
  showFileMenu.value = !showFileMenu.value
}

// Raw mode toggle
function toggleRawMode() {
  if (isRawMode.value) {
    // Switching from raw to WYSIWYG - convert markdown to HTML
    const result = markdownToHtml(rawMarkdown.value)
    handleConversionResult(result, 'Mode switch')
    editor.value?.commands.setContent(result.content)
    isRawMode.value = false
  } else {
    // Switching from WYSIWYG to raw - convert HTML to markdown
    const html = editor.value?.getHTML() || ''
    const result = htmlToMarkdown(html)
    handleConversionResult(result, 'Mode switch')
    rawMarkdown.value = result.content
    isRawMode.value = true
    // Focus textarea after Vue renders it
    setTimeout(() => {
      rawTextarea.value?.focus()
    }, 50)
  }
}

// Handle raw markdown changes
function onRawMarkdownInput(event: Event) {
  const target = event.target as HTMLTextAreaElement
  rawMarkdown.value = target.value
  hasUnsavedChanges.value = true
}

// Format last saved time
const lastSavedFormatted = computed(() => {
  if (!lastSaved.value) return null
  const now = new Date()
  const diff = Math.floor((now.getTime() - lastSaved.value.getTime()) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  return lastSaved.value.toLocaleTimeString()
})

// Keyboard shortcuts
function handleKeydown(event: KeyboardEvent) {
  const isMod = event.ctrlKey || event.metaKey

  // Ctrl/Cmd + ? - Show keyboard shortcuts help
  if (isMod && event.shiftKey && event.key === '?') {
    event.preventDefault()
    showShortcutsModal.value = !showShortcutsModal.value
    return
  }

  // Escape - Close modals/menus
  if (event.key === 'Escape') {
    if (showShortcutsModal.value) {
      showShortcutsModal.value = false
      return
    }
    if (showFileMenu.value) {
      showFileMenu.value = false
      return
    }
  }

  // File operations
  // Ctrl/Cmd + S - Save
  if (isMod && !event.shiftKey && event.key === 's') {
    event.preventDefault()
    saveToStorage()
    return
  }
  // Ctrl/Cmd + O - Open
  if (isMod && !event.shiftKey && event.key === 'o') {
    event.preventDefault()
    openFile()
    return
  }
  // Ctrl/Cmd + Shift + S - Export as Markdown
  if (isMod && event.shiftKey && event.key === 'S') {
    event.preventDefault()
    exportMarkdown()
    return
  }
  // Ctrl/Cmd + Shift + E - Export as HTML
  if (isMod && event.shiftKey && event.key === 'E') {
    event.preventDefault()
    exportHtml()
    return
  }
  // Ctrl/Cmd + N - New file
  if (isMod && event.key === 'n') {
    event.preventDefault()
    newFile()
    return
  }
  // Ctrl/Cmd + Shift + M - Toggle raw mode
  if (isMod && event.shiftKey && event.key === 'M') {
    event.preventDefault()
    toggleRawMode()
    return
  }

  // Skip formatting shortcuts if in raw mode
  if (isRawMode.value) return

  // Heading shortcuts (Ctrl/Cmd + Alt + 1/2/3/4/5/6)
  if (isMod && event.altKey && event.key >= '1' && event.key <= '6') {
    event.preventDefault()
    const level = parseInt(event.key) as 1 | 2 | 3 | 4 | 5 | 6
    toggleHeading(level)
    return
  }

  // Text formatting shortcuts
  // Ctrl/Cmd + Shift + X - Strikethrough
  if (isMod && event.shiftKey && event.key === 'X') {
    event.preventDefault()
    toggleStrike()
    return
  }
  // Ctrl/Cmd + E - Inline code
  if (isMod && !event.shiftKey && event.key === 'e') {
    event.preventDefault()
    toggleCode()
    return
  }
  // Ctrl/Cmd + K - Add link
  if (isMod && event.key === 'k') {
    event.preventDefault()
    setLink()
    return
  }

  // List shortcuts
  // Ctrl/Cmd + Shift + 8 - Bullet list
  if (isMod && event.shiftKey && event.key === '*') {
    event.preventDefault()
    toggleBulletList()
    return
  }
  // Ctrl/Cmd + Shift + 7 - Ordered list
  if (isMod && event.shiftKey && event.key === '&') {
    event.preventDefault()
    toggleOrderedList()
    return
  }
  // Ctrl/Cmd + Shift + 9 - Task list
  if (isMod && event.shiftKey && event.key === '(') {
    event.preventDefault()
    toggleTaskList()
    return
  }

  // Block shortcuts
  // Ctrl/Cmd + Shift + B - Blockquote
  if (isMod && event.shiftKey && event.key === 'B') {
    event.preventDefault()
    toggleBlockquote()
    return
  }
  // Ctrl/Cmd + Alt + C - Code block
  if (isMod && event.altKey && event.key === 'c') {
    event.preventDefault()
    toggleCodeBlock()
    return
  }
  // Ctrl/Cmd + Enter - Horizontal rule
  if (isMod && event.key === 'Enter') {
    event.preventDefault()
    addHorizontalRule()
    return
  }

  // Table shortcut
  // Ctrl/Cmd + Alt + T - Insert table
  if (isMod && event.altKey && event.key === 't') {
    event.preventDefault()
    insertTable()
    return
  }

  // Image shortcut
  // Ctrl/Cmd + Shift + I - Insert image
  if (isMod && event.shiftKey && event.key === 'I') {
    event.preventDefault()
    addImage()
    return
  }
}

// Close file menu when clicking outside
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (showFileMenu.value && !target.closest('.file-menu-container')) {
    showFileMenu.value = false
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('click', handleClickOutside)
  editor.value?.destroy()
})

defineExpose({
  editor,
  isActive,
})
</script>

<template>
  <div class="editor-wrapper group">
    <!-- Toast notifications -->
    <div class="toast-container">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="toast"
        :class="toast.type"
        @click="dismissToast(toast.id)"
      >
        <span class="toast-icon">
          <template v-if="toast.type === 'error'">&#x2716;</template>
          <template v-else-if="toast.type === 'warning'">&#x26A0;</template>
          <template v-else>&#x2714;</template>
        </span>
        <span class="toast-message">{{ toast.message }}</span>
        <button class="toast-dismiss" @click.stop="dismissToast(toast.id)">&#x2715;</button>
      </div>
    </div>
    <div class="toolbar-hover-reveal" v-if="editor">
      <!-- File menu -->
      <div class="file-menu-container relative">
        <button
          @click.stop="toggleFileMenu"
          class="file-menu-btn"
          :class="{ active: showFileMenu }"
          title="File Menu"
        >
          <span class="filename">{{ currentFilename }}</span>
          <span v-if="hasUnsavedChanges" class="unsaved-indicator">*</span>
        </button>
        <div v-if="showFileMenu" class="dropdown-menu">
          <button @click="newFile" class="menu-item">
            <span class="menu-icon">+</span>
            <span>New</span>
            <span class="shortcut">Ctrl+N</span>
          </button>
          <button @click="openFile" class="menu-item">
            <span class="menu-icon">📂</span>
            <span>Open</span>
            <span class="shortcut">Ctrl+O</span>
          </button>
          <button @click="saveToStorage" class="menu-item">
            <span class="menu-icon">💾</span>
            <span>Save</span>
            <span class="shortcut">Ctrl+S</span>
          </button>
          <button @click="renameFile" class="menu-item">
            <span class="menu-icon">✏️</span>
            <span>Rename</span>
          </button>
          <div class="menu-divider"></div>
          <button @click="exportMarkdown" class="menu-item">
            <span class="menu-icon">📄</span>
            <span>Export as .md</span>
            <span class="shortcut">Ctrl+Shift+S</span>
          </button>
          <button @click="exportHtml" class="menu-item">
            <span class="menu-icon">🌐</span>
            <span>Export as .html</span>
            <span class="shortcut">Ctrl+Shift+E</span>
          </button>
        </div>
      </div>

      <div class="toolbar-divider"></div>

      <div class="toolbar-group">
        <button
          @click="toggleHeading(1)"
          class="toolbar-btn"
          :class="{ active: isActive.heading1 }"
          title="Heading 1 (Ctrl+Alt+1)"
        >
          H1
        </button>
        <button
          @click="toggleHeading(2)"
          class="toolbar-btn"
          :class="{ active: isActive.heading2 }"
          title="Heading 2 (Ctrl+Alt+2)"
        >
          H2
        </button>
        <button
          @click="toggleHeading(3)"
          class="toolbar-btn"
          :class="{ active: isActive.heading3 }"
          title="Heading 3 (Ctrl+Alt+3)"
        >
          H3
        </button>
      </div>

      <div class="toolbar-divider"></div>

      <div class="toolbar-group">
        <button
          @click="toggleBold"
          class="toolbar-btn"
          :class="{ active: isActive.bold }"
          title="Bold (Ctrl+B)"
        >
          <strong>B</strong>
        </button>
        <button
          @click="toggleItalic"
          class="toolbar-btn"
          :class="{ active: isActive.italic }"
          title="Italic (Ctrl+I)"
        >
          <em>I</em>
        </button>
        <button
          @click="toggleStrike"
          class="toolbar-btn"
          :class="{ active: isActive.strike }"
          title="Strikethrough (Ctrl+Shift+X)"
        >
          <s>S</s>
        </button>
        <button
          @click="toggleCode"
          class="toolbar-btn"
          :class="{ active: isActive.code }"
          title="Inline Code (Ctrl+E)"
        >
          &lt;/&gt;
        </button>
      </div>

      <div class="toolbar-divider"></div>

      <div class="toolbar-group">
        <button
          @click="toggleBulletList"
          class="toolbar-btn"
          :class="{ active: isActive.bulletList }"
          title="Bullet List (Ctrl+Shift+8)"
        >
          •
        </button>
        <button
          @click="toggleOrderedList"
          class="toolbar-btn"
          :class="{ active: isActive.orderedList }"
          title="Ordered List (Ctrl+Shift+7)"
        >
          1.
        </button>
        <button
          @click="toggleTaskList"
          class="toolbar-btn"
          :class="{ active: isActive.taskList }"
          title="Task List (Ctrl+Shift+9)"
        >
          ☑
        </button>
      </div>

      <div class="toolbar-divider"></div>

      <div class="toolbar-group">
        <button
          @click="toggleBlockquote"
          class="toolbar-btn"
          :class="{ active: isActive.blockquote }"
          title="Blockquote (Ctrl+Shift+B)"
        >
          "
        </button>
        <button
          @click="toggleCodeBlock"
          class="toolbar-btn"
          :class="{ active: isActive.codeBlock }"
          title="Code Block (Ctrl+Alt+C)"
        >
          { }
        </button>
        <button
          @click="addHorizontalRule"
          class="toolbar-btn"
          title="Horizontal Rule"
        >
          ―
        </button>
      </div>

      <div class="toolbar-divider"></div>

      <div class="toolbar-group">
        <button
          @click="setLink"
          class="toolbar-btn"
          :class="{ active: isActive.link }"
          title="Add Link (Ctrl+K)"
        >
          🔗
        </button>
        <button
          @click="addImage"
          class="toolbar-btn"
          title="Add Image"
        >
          🖼
        </button>
        <button
          @click="insertTable"
          class="toolbar-btn"
          title="Insert Table"
        >
          ⊞
        </button>
      </div>

      <div class="toolbar-divider"></div>

      <!-- View mode toggle -->
      <div class="toolbar-group">
        <button
          @click="toggleRawMode"
          class="toolbar-btn mode-toggle-btn"
          :class="{ active: isRawMode }"
          :title="isRawMode ? 'Switch to WYSIWYG (Ctrl+Shift+M)' : 'Switch to Raw Markdown (Ctrl+Shift+M)'"
        >
          <span v-if="isRawMode">WYSIWYG</span>
          <span v-else>RAW</span>
        </button>
      </div>
    </div>

    <!-- WYSIWYG Editor (shown when not in raw mode) -->
    <EditorContent v-show="!isRawMode" :editor="editor" class="editor-content" />

    <!-- Raw Markdown Editor (shown when in raw mode) -->
    <textarea
      v-show="isRawMode"
      ref="rawTextarea"
      :value="rawMarkdown"
      @input="onRawMarkdownInput"
      class="raw-editor"
      placeholder="Write your markdown here..."
      spellcheck="false"
    ></textarea>

    <!-- Status bar -->
    <div class="status-bar">
      <span v-if="lastSavedFormatted" class="save-status">
        Saved {{ lastSavedFormatted }}
      </span>
      <span v-else-if="hasUnsavedChanges" class="save-status unsaved">
        Unsaved changes
      </span>
      <span class="mode-indicator" :class="{ raw: isRawMode }">
        {{ isRawMode ? 'RAW' : 'WYSIWYG' }}
      </span>
      <button class="keyboard-hints" @click="showShortcutsModal = true">
        Ctrl+? Shortcuts
      </button>
    </div>

    <!-- Keyboard Shortcuts Modal -->
    <Teleport to="body">
      <div v-if="showShortcutsModal" class="shortcuts-modal-overlay" @click.self="showShortcutsModal = false">
        <div class="shortcuts-modal">
          <div class="shortcuts-header">
            <h2>Keyboard Shortcuts</h2>
            <button class="shortcuts-close" @click="showShortcutsModal = false" title="Close (Esc)">
              &times;
            </button>
          </div>
          <div class="shortcuts-content">
            <!-- File Operations -->
            <div class="shortcuts-section">
              <h3>File Operations</h3>
              <div class="shortcut-list">
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>N</kbd></span>
                  <span class="shortcut-desc">New file</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>O</kbd></span>
                  <span class="shortcut-desc">Open file</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>S</kbd></span>
                  <span class="shortcut-desc">Save</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>S</kbd></span>
                  <span class="shortcut-desc">Export as Markdown</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>E</kbd></span>
                  <span class="shortcut-desc">Export as HTML</span>
                </div>
              </div>
            </div>

            <!-- View & Mode -->
            <div class="shortcuts-section">
              <h3>View & Mode</h3>
              <div class="shortcut-list">
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>M</kbd></span>
                  <span class="shortcut-desc">Toggle WYSIWYG/Raw mode</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>?</kbd></span>
                  <span class="shortcut-desc">Show this help</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Esc</kbd></span>
                  <span class="shortcut-desc">Close modal/menu</span>
                </div>
              </div>
            </div>

            <!-- Text Formatting -->
            <div class="shortcuts-section">
              <h3>Text Formatting</h3>
              <div class="shortcut-list">
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>B</kbd></span>
                  <span class="shortcut-desc">Bold</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>I</kbd></span>
                  <span class="shortcut-desc">Italic</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>X</kbd></span>
                  <span class="shortcut-desc">Strikethrough</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>E</kbd></span>
                  <span class="shortcut-desc">Inline code</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>K</kbd></span>
                  <span class="shortcut-desc">Add link</span>
                </div>
              </div>
            </div>

            <!-- Headings -->
            <div class="shortcuts-section">
              <h3>Headings</h3>
              <div class="shortcut-list">
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Alt</kbd><kbd>1</kbd></span>
                  <span class="shortcut-desc">Heading 1</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Alt</kbd><kbd>2</kbd></span>
                  <span class="shortcut-desc">Heading 2</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Alt</kbd><kbd>3</kbd></span>
                  <span class="shortcut-desc">Heading 3</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Alt</kbd><kbd>4-6</kbd></span>
                  <span class="shortcut-desc">Heading 4-6</span>
                </div>
              </div>
            </div>

            <!-- Lists -->
            <div class="shortcuts-section">
              <h3>Lists</h3>
              <div class="shortcut-list">
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>8</kbd></span>
                  <span class="shortcut-desc">Bullet list</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>7</kbd></span>
                  <span class="shortcut-desc">Ordered list</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>9</kbd></span>
                  <span class="shortcut-desc">Task list</span>
                </div>
              </div>
            </div>

            <!-- Blocks -->
            <div class="shortcuts-section">
              <h3>Blocks & Media</h3>
              <div class="shortcut-list">
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>B</kbd></span>
                  <span class="shortcut-desc">Blockquote</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Alt</kbd><kbd>C</kbd></span>
                  <span class="shortcut-desc">Code block</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Enter</kbd></span>
                  <span class="shortcut-desc">Horizontal rule</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Alt</kbd><kbd>T</kbd></span>
                  <span class="shortcut-desc">Insert table</span>
                </div>
                <div class="shortcut-item">
                  <span class="shortcut-keys"><kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>I</kbd></span>
                  <span class="shortcut-desc">Insert image</span>
                </div>
              </div>
            </div>
          </div>
          <div class="shortcuts-footer">
            <span class="shortcuts-note">On macOS, use <kbd>Cmd</kbd> instead of <kbd>Ctrl</kbd></span>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* Component-specific styles using CSS variables from main.css */
/* Most styles are now in the global CSS files (main.css and editor.css) */
/* This scoped section only contains component-specific overrides if needed */

/* Toast notification styles */
.toast-container {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: rgba(30, 41, 59, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.2);
  color: #e2e8f0;
  font-size: 0.875rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  pointer-events: auto;
  cursor: pointer;
  animation: toast-slide-in 0.3s ease-out;
  max-width: 400px;
}

@keyframes toast-slide-in {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.toast.error {
  border-color: rgba(239, 68, 68, 0.5);
  background: rgba(127, 29, 29, 0.9);
}

.toast.warning {
  border-color: rgba(234, 179, 8, 0.5);
  background: rgba(113, 63, 18, 0.9);
}

.toast.success {
  border-color: rgba(34, 197, 94, 0.5);
  background: rgba(20, 83, 45, 0.9);
}

.toast-icon {
  flex-shrink: 0;
  font-size: 1rem;
}

.toast.error .toast-icon {
  color: #fca5a5;
}

.toast.warning .toast-icon {
  color: #fcd34d;
}

.toast.success .toast-icon {
  color: #86efac;
}

.toast-message {
  flex: 1;
  line-height: 1.4;
}

.toast-dismiss {
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem;
  font-size: 1rem;
  line-height: 1;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.toast-dismiss:hover {
  opacity: 1;
}

/* Keyboard Shortcuts Modal */
.shortcuts-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(3, 7, 18, 0.85);
  backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: fade-in 0.2s ease-out;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.shortcuts-modal {
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(10, 22, 40, 0.98) 100%);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 1rem;
  width: 100%;
  max-width: 800px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5),
              0 0 0 1px rgba(255, 255, 255, 0.05),
              0 0 40px rgba(59, 130, 246, 0.15);
  animation: modal-slide-up 0.3s ease-out;
}

@keyframes modal-slide-up {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.shortcuts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
}

.shortcuts-header h2 {
  font-size: 1.375rem;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
  letter-spacing: -0.01em;
}

.shortcuts-close {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  color: #94a3b8;
  font-size: 1.75rem;
  line-height: 1;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.shortcuts-close:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.shortcuts-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

@media (max-width: 640px) {
  .shortcuts-content {
    grid-template-columns: 1fr;
  }
}

.shortcuts-section {
  background: rgba(30, 58, 95, 0.2);
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 0.75rem;
  padding: 1rem;
}

.shortcuts-section h3 {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #60a5fa;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.75rem 0;
}

.shortcut-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.shortcut-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.375rem 0;
}

.shortcut-keys {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.shortcut-keys kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  height: 1.5rem;
  padding: 0 0.5rem;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  color: #e2e8f0;
  background: linear-gradient(180deg, rgba(51, 65, 85, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 0.375rem;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.shortcut-desc {
  font-size: 0.875rem;
  color: #cbd5e1;
  text-align: right;
}

.shortcuts-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(148, 163, 184, 0.15);
  text-align: center;
}

.shortcuts-note {
  font-size: 0.8125rem;
  color: #64748b;
}

.shortcuts-note kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.25rem;
  padding: 0 0.375rem;
  font-family: inherit;
  font-size: 0.6875rem;
  font-weight: 500;
  color: #94a3b8;
  background: rgba(51, 65, 85, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 0.25rem;
  margin: 0 0.125rem;
}

/* Make keyboard-hints clickable button look like text */
button.keyboard-hints {
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s ease;
}

button.keyboard-hints:hover {
  color: #60a5fa;
}
</style>
